import { test, expect, equals, is } from "@benchristel/taste"
import {
  applyTo as pass,
  pipe,
  pickBy,
  map,
  toPairs,
  sortBy,
  prop,
  filter,
  concat,
  __,
} from "ramda"
import type { Item } from "src/EditableList"
import type { Atom } from "src/lib/atom-crdt"
import type { BallotBox } from "."
import { Candidate, emptyPoll, PollCRDT, Slate } from "./PollCRDT"
import {
  withBallot,
  withCandidate,
  withCandidateDeleted,
  withTitle,
} from "./transformers"

export type PBallotBox = { [agentID: string]: Candidate[] }

export type PollPhenomenon = {
  title: string | null
  candidates: Candidate[]
  id: string
  ballots: PBallotBox
}
const denormalize =
  (slate: Slate) =>
  (id: string): Candidate => ({ id, content: slate[id] || "" })

export const phenom = ({
  ballots,
  candidates,
  initial,
  id,
  title: { value: title },
}: PollCRDT & { id: string }): PollPhenomenon => ({
  id,
  title: initial ? null : title,
  candidates: phenomenalizeCandidates(extant(candidates)),
  ballots: map<BallotBox, Candidate[]>(
    phenomenalizeBallot(extant(candidates)),
    ballots,
  ),
})

const phenomenalizeCandidates = (candidates: Slate): Candidate[] =>
  toPairs(candidates).map(collate)

const phenomenalizeBallot =
  (candidates: Slate) =>
  ({ value: rankedCandidateIDs }: Atom<string[]>): Candidate[] =>
    pass(
      rankedCandidateIDs,
      pipe(
        filter((id: string) => id in candidates),
        map(denormalize(candidates)),
        concat(
          __,
          pass(
            candidates,
            pipe(
              toPairs,
              map(collate),
              filter(({ id }: Item) => !rankedCandidateIDs.includes(id)),
              sortBy(prop("id")),
            ),
          ),
        ),
      ),
    )

export function ballotFor(agentID: string, poll: PollPhenomenon): Candidate[] {
  return poll.ballots[agentID] || sortBy(prop("id"), poll.candidates)
}

function intersectionPreservingOrder<T>(as: T[], bs: T[]): T[] {
  const results = []
  const bsSet = new Set(bs)
  for (let a of as) {
    if (bsSet.has(a)) {
      results.push(a)
    }
  }
  return results
}

test("intersectionPreservingOrder", {
  "preserves order of its first argument"() {
    expect(intersectionPreservingOrder([1, 2, 3], [3, 2]), equals, [2, 3])
  },
})

test("phenom().title", {
  "properly phenomenalizes titles"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(withTitle("Which burrito do you prefer?")),
    )
    expect(
      phenom({ ...poll, id: "" }).title,
      equals,
      "Which burrito do you prefer?",
    )
  },

  "is null for polls in the `initial` state"() {
    // this ensures that loading states are displayed
    expect(phenom({ ...emptyPoll(), id: "" }).title, is, null)
  },
})

test("phenom().ballots", {
  "includes a ballot per voter"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({ id: "foo", content: "Mr. Foo" }),
        withCandidate({ id: "bar", content: "Madame Bar" }),
        withBallot("007", ["foo", "bar"]),
      ),
    )

    expect(phenom({ ...poll, id: "" }).ballots, equals, {
      "007": [
        { id: "foo", content: "Mr. Foo" },
        { id: "bar", content: "Madame Bar" },
      ],
    })
  },

  "excludes deleted candidates"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({ id: "top-gun", content: "" }),
        withCandidate({ id: "12-angry-men", content: "" }),
        withCandidate({ id: "when-harry-met-sally", content: "" }),
        withCandidateDeleted("top-gun"),
        withBallot("garp", ["top-gun", "when-harry-met-sally", "12-angry-men"]),
      ),
    )
    expect(phenom({ ...poll, id: "" }).ballots["garp"], equals, [
      { content: "", id: "when-harry-met-sally" },
      { content: "", id: "12-angry-men" },
    ])
  },

  "excludes non-existent candidates"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withBallot("garp", ["top-gun", "12-angry-men", "when-harry-met-sally"]),
      ),
    )
    const phenomenon = phenom({ ...poll, id: "movies-to-get-at-blockbuster" })
    expect(phenomenon.ballots["garp"], equals, [])
  },

  "appends candidates I have not ranked to my ballot"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({ id: "a", content: "" }),
        withCandidate({ id: "b", content: "" }),
        withCandidate({ id: "c", content: "" }),
        withCandidate({ id: "ranked-one", content: "" }),
        withCandidate({ id: "ranked-two", content: "" }),
        withBallot("007", ["ranked-one", "ranked-two"]),
      ),
    )
    expect(
      phenom({ ...poll, id: "" }).ballots["007"]?.map(prop("id")),
      equals,
      ["ranked-one", "ranked-two", "a", "b", "c"],
    )
  },

  "sorts unranked candidates by ID"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({ id: "c", content: "" }),
        withCandidate({ id: "ranked-two", content: "" }),
        withCandidate({ id: "a", content: "" }),
        withCandidate({ id: "b", content: "" }),
        withCandidate({ id: "ranked-one", content: "" }),
        withBallot("007", ["ranked-one", "ranked-two"]),
      ),
    )
    expect(
      phenom({ ...poll, id: "" }).ballots["007"]?.map(prop("id")),
      equals,
      ["ranked-one", "ranked-two", "a", "b", "c"],
    )
  },

  "shows me candidates before I've voted"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({ id: "foo", content: "Mr. Foo" }),
        withCandidate({ id: "bar", content: "Madame Bar" }),
      ),
    )
    expect(ballotFor("007", phenom({ ...poll, id: "" })), equals, [
      { id: "bar", content: "Madame Bar" },
      { id: "foo", content: "Mr. Foo" },
    ])
  },
})

test("phenom().candidates", {
  "excludes deleted candidates"() {
    const poll: PollCRDT = pass(
      emptyPoll(),
      pipe(
        withCandidate({
          id: "fun-activity-12",
          content: "bowling",
        }),
        withCandidateDeleted("fun-activity-12"),
      ),
    )

    expect(phenom({ ...poll, id: "bachelorette-party" }).candidates, equals, [])
  },
})

const collate = ([id, content]: [string, string | null]): Item => ({
  id,
  content,
})

const notNull = (content: any): boolean => content !== null
const extant = (candidates: Slate): Slate => pickBy(notNull, candidates)

test("hasContent", {
  "returns false if content is null"() {
    expect(notNull(null), is, false)
  },
  "returns true if content is empty string"() {
    expect(notNull(""), is, true)
  },
})
